import type { BuilderContent } from "@builder.io/sdk-react";
import { fetchOneEntry, getBuilderSearchParams } from "@builder.io/sdk-react";

import { createFetchCacheConfig } from "@/libs/cache-config/fetch/createFetchCacheConfig";
import type { FetchCacheTag } from "@/libs/cache-config/fetch/tags";
import { createBuilderFetchCacheTags } from "@/libs/cache-config/fetch/tags";

import { env } from "../../../env";
import type { BuilderModels } from "../builder.models.types";

import { sanitizeBuilderContent } from "./sanitizeBuilderContent";

export type FetchOneEntryParams = Parameters<typeof fetchOneEntry>;

export type FetchOneEntryQuery = FetchOneEntryParams[0]["query"];

type FetchOneEntryOptions = Omit<FetchOneEntryParams[0], "apiKey" | "locale" | "model"> & {
  model: BuilderModels;
  locale?: string;
  bypassCache?: boolean;
  publicUrlPath?: string;
  cacheTags?: FetchCacheTag[];
};

type BuilderContentWithQuery = BuilderContent & {
  query?: FetchOneEntryQuery[];
};

export type FetchOneBuilderEntryResponse = BuilderContentWithQuery | null;

export const fetchOneBuilderEntry = async ({
  locale,
  options,
  userAttributes,
  bypassCache,
  publicUrlPath,
  cacheTags,
  ...restParams
}: FetchOneEntryOptions): Promise<FetchOneBuilderEntryResponse> => {
  const resolvedUrlPath = typeof userAttributes?.urlPath === "string" ? userAttributes.urlPath : undefined;
  const resolvedLocale = locale ?? (typeof userAttributes?.locale === "string" ? userAttributes.locale : undefined);

  const content = await fetchOneEntry({
    fetch: bypassCache
      ? undefined
      : createBuilderFetchWithCache({
          modelName: restParams.model,
          locale: resolvedLocale,
          urlPath: resolvedUrlPath,
          cacheTags,
        }),
    apiKey: env.builderApiKey,
    options: {
      ...getBuilderSearchParams(options),
      cachebust: true, // intentionally disable cache
    },
    cacheSeconds: 0, // intentionally disable cache
    locale,
    userAttributes: {
      locale,
      ...userAttributes,
    },
    ...restParams,
  });

  return sanitizeBuilderContent(
    content,
    publicUrlPath && userAttributes?.urlPath
      ? {
          internalUrlPath: userAttributes.urlPath,
          publicUrlPath,
        }
      : undefined
  );
};

const createBuilderFetchWithCache = ({
  modelName,
  locale,
  urlPath,
  cacheTags,
}: {
  modelName: BuilderModels;
  locale?: string;
  urlPath?: string;
  cacheTags?: FetchCacheTag[];
}) => {
  const tags = cacheTags ?? createBuilderFetchCacheTags({ modelName, locale, urlPath });

  return (input: string, init?: object | undefined) => {
    return fetch(input, {
      ...createFetchCacheConfig({ tags }),
      ...init,
    });
  };
};
